/**
 * PDF export for travel bonuses (Primes de petit déplacement).
 * One page per employee with KPIs, breakdown, detail, and signature block.
 */
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const MONTHS_FR = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

const fmtEur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);

/**
 * Render a single employee block starting at the current pdf cursor.
 * @returns {jsPDF} the same pdf instance
 */
function renderEmployee(pdf, emp, entries, periodLabel, hqLabel) {
  // Header
  pdf.setFontSize(16);
  pdf.setFont("helvetica", "bold");
  pdf.text(`Bulletin de primes de petit déplacement`, 14, 16);

  pdf.setFontSize(11);
  pdf.text(`${emp.employee_name}`, 14, 24);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.text(`Période : ${periodLabel}`, 14, 30);
  pdf.text(`Siège : ${hqLabel}`, 14, 35);
  pdf.text(`Édité le ${new Date().toLocaleDateString("fr-FR")}`, 14, 40);

  // Synthesis
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.text(`Nb primes : ${emp.count}    |    Montant total : ${fmtEur(emp.amount)}`, 14, 48);

  // By zone
  const zoneRows = [1, 2, 3, 4, 5].map((z) => [
    `Zone ${z}`,
    ["0-10 km", "10-20 km", "20-30 km", "30-40 km", "40-50 km"][z - 1],
    String(emp.by_zone?.[String(z)]?.count || 0),
    fmtEur(emp.by_zone?.[String(z)]?.amount || 0)
  ]);
  zoneRows.push(["Total", "", String(emp.count), fmtEur(emp.amount)]);

  autoTable(pdf, {
    startY: 52,
    head: [["Zone", "Distance", "Nb primes", "Montant"]],
    body: zoneRows,
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: [234, 88, 12] },
    columnStyles: {
      0: { cellWidth: 30, fontStyle: "bold" },
      1: { cellWidth: 40 },
      2: { cellWidth: 30, halign: "right" },
      3: { cellWidth: 40, halign: "right", fontStyle: "bold" }
    },
    didParseCell: (d) => {
      if (d.row.index === zoneRows.length - 1) {
        d.cell.styles.fillColor = [245, 245, 245];
        d.cell.styles.fontStyle = "bold";
      }
    }
  });

  // Monthly breakdown if available (annual view)
  if (emp.by_month && emp.by_month.length > 1) {
    const monthRows = emp.by_month.map((m) => [MONTHS_FR[m.month], String(m.count), fmtEur(m.amount)]);
    autoTable(pdf, {
      startY: pdf.lastAutoTable.finalY + 4,
      head: [["Mois", "Nb primes", "Montant"]],
      body: monthRows,
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [234, 88, 12] },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 40, halign: "right" },
        2: { cellWidth: 50, halign: "right", fontStyle: "bold" }
      }
    });
  }

  // Detail
  const empEntries = (entries || []).filter((e) => e.employee_id === emp.employee_id);
  if (empEntries.length > 0) {
    const detailRows = empEntries.map((d) => [
      d.date,
      d.order_reference || "-",
      d.city || "-",
      d.distance_km != null ? `${d.distance_km} km` : "-",
      d.zone ? `Zone ${d.zone}` : (d.error ? "Erreur" : "-"),
      fmtEur(d.amount)
    ]);
    autoTable(pdf, {
      startY: pdf.lastAutoTable.finalY + 4,
      head: [["Date", "Commande", "Ville chantier", "Distance", "Zone", "Montant"]],
      body: detailRows,
      styles: { fontSize: 8, cellPadding: 1.6 },
      headStyles: { fillColor: [234, 88, 12] },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 45 },
        2: { cellWidth: 50 },
        3: { cellWidth: 22, halign: "right" },
        4: { cellWidth: 22, halign: "center" },
        5: { cellWidth: 28, halign: "right", fontStyle: "bold" }
      }
    });
  }

  // Signature block
  const pageH = pdf.internal.pageSize.getHeight();
  const sigY = pageH - 30;
  pdf.setDrawColor(120);
  pdf.setFontSize(9);
  pdf.setFont("helvetica", "bold");
  pdf.text("Signature du salarié :", 20, sigY);
  pdf.line(20, sigY + 16, 95, sigY + 16);
  pdf.text("Signature du responsable :", 110, sigY);
  pdf.line(110, sigY + 16, 185, sigY + 16);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.text("Date : ____________________", 20, sigY + 22);
  pdf.text("Date : ____________________", 110, sigY + 22);

  return pdf;
}

/**
 * Export a PDF for one or several employees.
 *
 * @param {object} data  - compute response from /api/travel-bonuses/compute
 * @param {object} opts  - { year, month, employeeId (optional) }
 */
export function exportTravelBonusPDF(data, opts = {}) {
  const { year, month, employeeId } = opts;
  const periodLabel = month ? `${MONTHS_FR[month]} ${year}` : `Année ${year}`;
  const hqLabel = data?.hq ? `${data.hq.city} (${data.hq.postal_code})` : "-";

  const targets = employeeId
    ? (data.by_employee || []).filter((e) => e.employee_id === employeeId)
    : (data.by_employee || []);

  if (targets.length === 0) return null;

  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  targets.forEach((emp, idx) => {
    if (idx > 0) pdf.addPage();
    renderEmployee(pdf, emp, data.per_day, periodLabel, hqLabel);
  });

  const suffix = month ? `${year}-${String(month).padStart(2, "0")}` : String(year);
  const fname = targets.length === 1
    ? `primes-deplacement_${targets[0].employee_name.replace(/\s+/g, "-")}_${suffix}.pdf`
    : `primes-deplacement_tous_${suffix}.pdf`;

  pdf.save(fname);
  return fname;
}
